/* eslint-disable no-restricted-globals */

import { getMeristemContext, setContextType } from "src/utils/meristemContext";
import store from "src/utils/redux/store";
import { captureException } from "src/utils/sentry";
import { getSearchQuery } from "src/utils/urlParams";
import { isHM, isInApp } from "src/utils/userSegment";
import {
  isDevMode,
  isInAppNativePurchaseEligible,
} from "src/utils/userSegment/features";
import goto from ".";
import { isBuyflow } from "./buyflow";
import {
  isInsideAndroidWebView,
  isInsideIOSWebKit,
  pushReroll,
  setParam,
} from "./utils";

declare const Android: any;

// "/purchase/:planId"
// "/purchase/(en|es|de)/:planId"
// "/purchase-hm/:planId"
function isPurchase() {
  return getMeristemContext().context_type === "payment";
}

function purchaseRoot(planId?: string) {
  const state = store.getState();
  const {
    language,
    serverContext,
    userData,
    geoLocation,
    routeId,
    recommendedPlan,
  } = state;

  const root = `/purchase${isHM(state) ? "-hm" : ""}`;

  const pathMatch = /^\/purchase(?:-hm)?(?:\/(?:en|es|de))?\/(.*)/.exec(
    location.pathname
  );
  const finalPlanId = planId || recommendedPlan.noom_plan_id || pathMatch?.[1];

  /* istanbul ignore next sanity */
  if (!finalPlanId) {
    throw new Error("No plan id provided");
  }

  let purchaseUrl =
    serverContext.PURCHASE_URL ??
    (isHM(state) ? `${root}/` : `${root}/${language || "en"}/`);
  purchaseUrl += finalPlanId;

  const queryParams = new URLSearchParams();

  // Add route to purchase page url params since cookies don't work 100% of the time.
  setParam(queryParams, "route", routeId);

  // Add fcc if exists, for the in-app buyflows, in order to enforce location based on device locale.
  if (geoLocation?.is_forced_country_code) {
    setParam(queryParams, "fcc", geoLocation.country_code);
  }

  const currentUrlParams = new URLSearchParams(window.location.search);
  currentUrlParams.forEach((value, key) => {
    queryParams.set(key, value);
  });

  // (justin) params not necessary if not in telehealth flow
  queryParams.delete("userId");
  queryParams.delete("noomPlanId");

  if (isInApp()) {
    setParam(queryParams, "email", userData.email);
  }

  if (isDevMode()) {
    setParam(queryParams, "idm", true);
  }
  return `${purchaseUrl.replace(/\/$/, "")}/${getSearchQuery(queryParams)}`;
}

export default {
  purchaseSurvey(planId?: string, question?: string) {
    const url = `${purchaseRoot(planId)}#/createAccount${
      question ? `/${question}` : ""
    }`;

    // Since we don't reroll going into payment funnel at this point,
    // we need to reset the context type to ensure we load the appropriate
    // server context on nav.
    if (isBuyflow()) {
      setContextType("payment");
    }
    pushReroll(url, () => isPurchase());
  },

  welcomeToNoom() {
    pushReroll(`${purchaseRoot()}#/welcomeToNoom`, isPurchase);
  },
  account() {
    pushReroll(`${purchaseRoot()}#/account`, isPurchase);
  },
  finalUpdateGraph() {
    pushReroll(`${purchaseRoot()}#/finalUpdateGraph`, isPurchase);
  },
  buildPlan() {
    pushReroll(`${purchaseRoot()}#/buildPlan`, isPurchase);
  },
  chooseTrialPrice(planId?: string) {
    pushReroll(`${purchaseRoot(planId)}#/chooseTrialPrice`, isPurchase);
  },
  payment() {
    pushReroll(`${purchaseRoot()}#/payment`, isPurchase);
  },
  paymentDetails(planId?: string) {
    pushReroll(`${purchaseRoot(planId)}#/details`, isPurchase);
  },
  programSwitchDetails() {
    pushReroll(`${purchaseRoot()}#/programSwitchDetails`, isPurchase);
  },
  programSwitchConfirmation() {
    pushReroll(`${purchaseRoot()}#/programSwitchConfirmation`, isPurchase);
  },
  stickToHWConfirmation() {
    pushReroll(`${purchaseRoot()}#/stickToHWConfirmation`, isPurchase);
  },

  nativePurchase() {
    if (isInsideIOSWebKit()) {
      try {
        // NOTE(norbert): the buyflowPurchaseNoDetails endpoint was only introduced
        // in the iOS 9.14 release, so we need to check if it exists before calling it
        if (
          isInAppNativePurchaseEligible() &&
          (window as any).webkit.messageHandlers.buyflowPurchaseNoDetails
        ) {
          // If app and route support web purchase, use that
          goto.buildPlan();
        } else {
          (window as any).webkit.messageHandlers.buyflowPurchase.postMessage(
            {}
          );
        }
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchase.ios");
      }
    }

    if (isInsideAndroidWebView()) {
      try {
        Android.buyflowPurchase();
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchase.android");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchase: no message handler found");

    return false;
  },
  nativePurchaseNoDetails() {
    if (
      isInAppNativePurchaseEligible() &&
      (window as any).webkit?.messageHandlers?.buyflowPurchaseNoDetails
    ) {
      try {
        (
          window as any
        ).webkit.messageHandlers.buyflowPurchaseNoDetails.postMessage({});
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseNoDetails.ios");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchaseNoDetails: no message handler found");

    return false;
  },
  nativePurchaseComplete() {
    if (isInsideIOSWebKit()) {
      try {
        (
          window as any
        ).webkit.messageHandlers.buyflowPurchaseCompleted.postMessage({});
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseComplete.ios");
      }
    }
    if (isInsideAndroidWebView()) {
      try {
        Android.buyflowPurchaseCompleted();
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseComplete.android");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchaseComplete: no message handler found");
    return false;
  },
};
